import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../api';

const SYNC_BTN_STYLE = {
  fontWeight: 700,
  fontSize: '13px',
  padding: '0.35rem 0.9rem',
  borderRadius: '6px',
  border: '2px solid #e65c00',
  background: 'linear-gradient(135deg,#ff8c00,#e65c00)',
  color: '#fff',
  boxShadow: '0 2px 6px rgba(230,92,0,0.4)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

function StatusBadge({ status }) {
  const map = {
    completed: ['bg-success', '✅ Completed'],
    running:   ['bg-warning text-dark', '⏳ Running'],
    failed:    ['bg-danger', '❌ Failed'],
  };
  const [cls, label] = map[status] || ['bg-secondary', status];
  return <span className={`badge ${cls}`}>{label}</span>;
}

function AttemptBadge({ status }) {
  const map = {
    sent:    ['bg-success', '✅ Sent'],
    failed:  ['bg-danger', '❌ Failed'],
    skipped: ['bg-secondary', '⏭ Skipped'],
    pending: ['bg-warning text-dark', '⏳ Pending'],
  };
  const [cls, label] = map[status] || ['bg-secondary', status];
  return <span className={`badge ${cls}`}>{label}</span>;
}

function JobRow({ job, onExpand, expanded }) {
  const runAt = job.run_at ? new Date(job.run_at).toLocaleString() : '—';
  const completedAt = job.completed_at ? new Date(job.completed_at).toLocaleString() : '—';
  const duration =
    job.run_at && job.completed_at
      ? `${Math.round((new Date(job.completed_at) - new Date(job.run_at)) / 1000)}s`
      : null;

  return (
    <>
      <tr
        style={{ cursor: 'pointer' }}
        onClick={() => onExpand(job.id)}
        title="Click to view port-by-port results"
      >
        <td>
          <span className="fw-bold">#{job.id}</span>{' '}
          <span className="badge bg-light text-dark border">{job.triggered_by}</span>
        </td>
        <td className="small">{runAt}</td>
        <td><StatusBadge status={job.status} /></td>
        <td className="text-center">{job.total_ports}</td>
        <td className="text-center text-success fw-bold">{job.total_sent}</td>
        <td className="text-center text-danger fw-bold">{job.total_failed}</td>
        <td className="text-center text-muted">{job.total_skipped}</td>
        <td className="small text-muted">{duration || completedAt}</td>
        <td className="text-muted small">{job.notes || '—'}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={9} className="p-0 bg-light border-top-0">
            <AttemptDetails jobId={job.id} />
          </td>
        </tr>
      )}
    </>
  );
}

function AttemptDetails({ jobId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    api
      .get(`/keep-alive/jobs/${jobId}`)
      .then((r) => setData(r.data))
      .catch(() => setErr('Failed to load details'));
  }, [jobId]);

  if (err) return <div className="p-3 text-danger small">{err}</div>;
  if (!data) return <div className="p-3 text-muted small">Loading…</div>;

  const attempts = data.attempts || [];
  if (attempts.length === 0)
    return <div className="p-3 text-muted small">No attempts recorded.</div>;

  const failed = attempts.filter((a) => a.status === 'failed');
  const sent = attempts.filter((a) => a.status === 'sent');
  const skipped = attempts.filter((a) => a.status === 'skipped');

  return (
    <div className="p-3">
      {/* Summary bar */}
      <div className="d-flex gap-3 mb-3 small">
        <span className="text-success fw-bold">✅ Sent: {sent.length}</span>
        <span className="text-danger fw-bold">❌ Failed: {failed.length}</span>
        <span className="text-muted">⏭ Skipped: {skipped.length}</span>
      </div>

      {failed.length > 0 && (
        <>
          <div className="fw-semibold text-danger small mb-1">Failed ports</div>
          <div className="table-responsive mb-3">
            <table className="table table-sm table-bordered mb-0" style={{ fontSize: '12px' }}>
              <thead className="table-light">
                <tr>
                  <th>Port</th>
                  <th>Assigned Number</th>
                  <th>Recipient</th>
                  <th>Message</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {failed.map((a) => (
                  <tr key={a.id}>
                    <td className="font-monospace">{a.port_number}</td>
                    <td className="font-monospace">{a.assigned_number}</td>
                    <td className="font-monospace">{a.recipient_number || '—'}</td>
                    <td className="text-muted">{a.message || '—'}</td>
                    <td className="text-danger">{a.error_message || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {sent.length > 0 && (
        <>
          <div className="fw-semibold text-success small mb-1">Successfully sent</div>
          <div className="table-responsive">
            <table className="table table-sm table-bordered mb-0" style={{ fontSize: '12px' }}>
              <thead className="table-light">
                <tr>
                  <th>Port</th>
                  <th>Assigned Number</th>
                  <th>Recipient</th>
                  <th>Message sent</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {sent.map((a) => (
                  <tr key={a.id}>
                    <td className="font-monospace">{a.port_number}</td>
                    <td className="font-monospace">{a.assigned_number}</td>
                    <td className="font-monospace">{a.recipient_number || '—'}</td>
                    <td className="text-muted">{a.message}</td>
                    <td className="small text-muted">
                      {a.attempted_at ? new Date(a.attempted_at).toLocaleTimeString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default function KeepAliveDashboard() {
  const role = localStorage.getItem('role');
  if (!localStorage.getItem('token') || role !== 'staff') {
    window.location.href = role === 'user' ? '/user' : '/userlogin';
    return null;
  }

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const [togglingPause, setTogglingPause] = useState(false);
  const [err, setErr] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const pollRef = useRef(null);

  const loadJobs = useCallback(async () => {
    try {
      const [jobsRes, statusRes] = await Promise.all([
        api.get('/keep-alive/jobs'),
        api.get('/keep-alive/status'),
      ]);
      setJobs(jobsRes.data || []);
      setPaused(statusRes.data?.paused ?? false);
    } catch {
      setErr('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleTogglePause = async () => {
    setTogglingPause(true);
    try {
      const res = await api.post(paused ? '/keep-alive/resume' : '/keep-alive/pause');
      setPaused(res.data?.paused ?? !paused);
    } catch {
      setErr('Failed to update automation state');
    } finally {
      setTogglingPause(false);
    }
  };

  // Auto-poll every 10s when a job is running
  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    const hasRunning = jobs.some((j) => j.status === 'running');
    if (hasRunning) {
      pollRef.current = setInterval(loadJobs, 10000);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [jobs, loadJobs]);

  const handleRunNow = async () => {
    if (running) return;
    setRunning(true);
    setErr('');
    try {
      await api.post('/keep-alive/run');
      await loadJobs();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setErr(
        detail
          ? `Error ${status}: ${detail}`
          : status
          ? `HTTP ${status} — backend may need a restart for new routes to load`
          : `Failed to start job: ${e?.message || 'unknown error'}`
      );
    } finally {
      setRunning(false);
    }
  };

  const toggleExpand = (id) => setExpandedId((prev) => (prev === id ? null : id));

  const lastJob = jobs[0];
  const hasRunning = jobs.some((j) => j.status === 'running');

  return (
    <div className="container-fluid px-3 py-3" style={{ fontSize: '13px' }}>
      {/* Header */}
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
        <div>
          <h4 className="mb-0" style={{ fontSize: '18px' }}>
            SIM Keep-Alive Automation
          </h4>
          <div className="text-muted small mt-1">
            Automatically sends a 4-word message from ports that haven't messaged in 80–90 days
            (or have never sent) to keep SIM cards active. Runs daily at 02:00 UTC.
          </div>
        </div>
        <div className="d-flex gap-2">
          <button
            style={{
              fontWeight: 700,
              fontSize: '13px',
              padding: '0.35rem 0.9rem',
              borderRadius: '6px',
              border: paused ? '2px solid #198754' : '2px solid #dc3545',
              background: paused ? '#198754' : '#dc3545',
              color: '#fff',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              opacity: togglingPause ? 0.7 : 1,
            }}
            onClick={handleTogglePause}
            disabled={togglingPause}
            title={paused ? 'Resume the daily automation' : 'Pause the daily automation (manual runs still work)'}
          >
            {togglingPause
              ? <><span className="spinner-border spinner-border-sm me-1" />…</>
              : paused ? '▶ Resume Schedule' : '⏸ Pause Schedule'
            }
          </button>
          <button
            style={running || hasRunning ? { ...SYNC_BTN_STYLE, opacity: 0.7 } : SYNC_BTN_STYLE}
            onClick={handleRunNow}
            disabled={running || hasRunning}
            title="Manually trigger a keep-alive run right now"
          >
            {running || hasRunning ? (
              <><span className="spinner-border spinner-border-sm me-1" />Running…</>
            ) : (
              '⚡ Run Now'
            )}
          </button>
        </div>
      </div>

      {paused && (
        <div className="alert alert-warning py-2 small mb-2">
          <strong>⏸ Schedule paused.</strong> The daily 02:00 UTC automation will not run until resumed. Manual runs still work.
        </div>
      )}
      {err && (
        <div className="alert alert-danger py-2 small">{err}</div>
      )}

      {/* Last run summary */}
      {lastJob && (
        <div
          className={`alert py-2 px-3 mb-3 small ${
            lastJob.status === 'completed'
              ? 'alert-success'
              : lastJob.status === 'running'
              ? 'alert-warning'
              : 'alert-danger'
          }`}
        >
          <strong>Last run:</strong>{' '}
          {new Date(lastJob.run_at).toLocaleString()} ({lastJob.triggered_by}) —{' '}
          ✅ {lastJob.total_sent} sent · ❌ {lastJob.total_failed} failed · ⏭ {lastJob.total_skipped} skipped
          {lastJob.status === 'running' && (
            <span className="ms-2">
              <span className="spinner-border spinner-border-sm me-1" />In progress…
            </span>
          )}
        </div>
      )}

      {/* Jobs table */}
      {loading ? (
        <div className="text-muted py-4 text-center">
          <span className="spinner-border spinner-border-sm me-2" />Loading jobs…
        </div>
      ) : jobs.length === 0 ? (
        <div className="alert alert-info py-2 small">
          No keep-alive jobs have run yet. Click <strong>Run Now</strong> to start the first one,
          or wait for the daily 02:00 UTC schedule.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-bordered align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Job</th>
                <th>Run at</th>
                <th>Status</th>
                <th className="text-center">Ports</th>
                <th className="text-center text-success">Sent</th>
                <th className="text-center text-danger">Failed</th>
                <th className="text-center text-muted">Skipped</th>
                <th>Completed</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <JobRow
                  key={job.id}
                  job={job}
                  expanded={expandedId === job.id}
                  onExpand={toggleExpand}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-muted mt-3" style={{ fontSize: '11px' }}>
        <strong>How it works:</strong> Each day at 02:00 UTC, the system finds all ports on Gateway 1
        and 2 where the last outbound message was 80–90 days ago (or has never been sent). It activates
        each port, sends a random 4-word message to a healthy port on the same gateway, then updates
        the Last Message Sent date. Failed ports are listed above. Click any row to see per-port results.
      </div>
    </div>
  );
}
